import React, { createRef, useCallback, useEffect, useState } from "react";
import { useRef } from "react";
import { useMemo } from "react";

import ArrowRight from "images/icons/arrow-right.inline.svg";
import CloseSVG from "images/icons/close.inline.svg";
import TickSVG from "images/icons/tick.inline.svg";

import { useOutsideClick } from "../../hooks/useOutsideClick";

function formatMenu(data) {
  const result = data.map((child) => ({
    // ...child,
    type: "item",
    label: child.label,
    value: child?.value ?? child?.label ?? "",
    filter: child?.name ?? child?.filter_name ?? "lost",
    list: child?.list ?? null,
    max: child?.max ?? null,
    min: child?.min ?? null
  }));

  for (const child of result) {
    if (child?.list && Array.isArray(child.list) && child.list.length) {
      child.type = child.list[0].list ? "filter-option" : "filter-suboption";
      child.list = formatMenu(child.list);
    }
  }

  return result;
}

export const FilterMenu = ({
  options,
  openSidebar,
  handleCloseSidebar,
  className,
  filterPrograms
}) => {
  const [title, setTitle] = useState("");
  const [menuData, setMenuData] = useState({});
  const [isOpened, setOpenArrow] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [selectedItems, setSelectedItems] = useState({});
  const filterSidebarRef = useRef(null);

  const refs = useMemo(
    () => Array.from({ length: menuData.length }).map(() => createRef()),
    [menuData.length]
  );

  const init = useCallback(() => {
    const formattedMenu = formatMenu(options);
    setMenuData(formattedMenu);
    setTitle("Filter By");
    setOpenArrow(false);
    setCurrentIndex(0);
    refs.map((ref) => (ref.current.style.height = 0));
  }, [options, refs]);

  useEffect(() => {
    setTitle("Filter By");
  }, []);

  useEffect(() => {
    const formattedMenu = formatMenu(options);
    setMenuData(formattedMenu);
  }, [options]);

  useEffect(() => {
    const tempSelectedItems = { ...selectedItems };

    options?.map((item) => {
      tempSelectedItems[item.name] = item?.defaultValue?.value ?? "";
    });

    setSelectedItems(tempSelectedItems);
  }, [options]);

  useEffect(() => {
    const filterObject = {
      programType: { value: selectedItems.programType ?? "" },
      programFocus: { value: selectedItems.programFocus ?? "" },
      programPrice: { ...(selectedItems.programPrice ?? "") },
      programDuration: { value: selectedItems.programDuration ?? "" },
      programLocation: { value: selectedItems.programLocation ?? "" }
    };

    if (filterPrograms)
      // We won't need this conditional statement after you implemented functionality to program list
      filterPrograms(filterObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  const handleMenuClick = useCallback(
    (option, index) => () => {
      if (option.type === "filter-option") {
        setTitle(option.label);
        setMenuData(option.list);
      } else if (option.type === "filter-suboption") {
        if (currentIndex === index) {
          if (refs[index].current.style.height !== "0px") {
            refs[index].current.style.height = 0;
            setOpenArrow(false);
          } else {
            refs[index].current.style.height =
              refs[index].current.scrollHeight + "px";
            setOpenArrow(true);
          }
          setCurrentIndex(index);
        } else {
          if (refs[currentIndex]) refs[currentIndex].current.style.height = 0;
          refs[index].current.style.height =
            refs[index].current.scrollHeight + "px";
          setOpenArrow(true);
          setCurrentIndex(index);
        }
      }
    },
    [currentIndex, refs]
  );

  const handleSelect = useCallback(
    (item) => () => {
      // console.debug("handleSelect >> item", item);
      // For multiple options
      // if (selectedItems[item.filter]?.includes(item.value)) {
      if (selectedItems[item.filter] === item.value) {
        const key = item.filter;
        delete selectedItems[key];
        const temp = { ...selectedItems };

        setSelectedItems(temp);
      } else {
        const temp = { ...selectedItems };
        if (!temp[item.filter] || temp[item.filter]?.length === 0) {
          temp[item.filter] = [];
        }
        // For multiple options
        // temp[item.filter].push(item.value);
        temp[item.filter] =
          item?.min && item?.max
            ? {
                value: item?.value,
                max: item?.max,
                min: item?.min
              }
            : item?.value;
        setSelectedItems(temp);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItems, title]
  );

  const handleClear = useCallback(() => {
    setSelectedItems({});
    filterPrograms("clear");
  }, [filterPrograms]);

  const handleClose = useCallback(() => {
    handleCloseSidebar();
    init();
  }, [handleCloseSidebar, init]);

  useOutsideClick(filterSidebarRef, (_) => {
    handleCloseSidebar();
    init();
  });

  const optionsBrief = useCallback(
    (type) => {
      const selectedOptions = selectedItems[type]?.join(" ");

      return selectedOptions?.length > 17
        ? selectedOptions?.substring(0, 17) + "..."
        : selectedOptions;
    },
    [selectedItems]
  );

  return (
    <div
      className={`fixed top-0 z-10 p-5 h-full w-11/12 bg-yellow-standard/90 planet:bg-white/10 backdrop-blur-xl overflow-scroll transition-all duration-200 ${
        openSidebar ? "right-0" : "-right-108"
      } ${className}`}
      ref={filterSidebarRef}
    >
      <div className="flex justify-between items-center py-2">
        <CloseSVG
          className="w-3 h-3 stroke-white planet:stroke-gray-generic"
          onClick={handleClose}
        />
        <div
          className="h7 uppercase !font-body !text-white"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <button
          className="text-xs text-white planet:text-gray-generic"
          onClick={handleClear}
        >
          Clear
        </button>
      </div>

      <div className="mt-5 divide-y width-divider !border-white planet:!border-gray-generic" />
      {menuData &&
        menuData?.length > 0 &&
        menuData?.map((menuOption, index) => (
          <React.Fragment key={index}>
            <button
              className="flex justify-between items-center w-full py-4"
              onClick={handleMenuClick(menuOption, index)}
            >
              <div className="flex text-xs">
                <div
                  className="text-white"
                  dangerouslySetInnerHTML={{ __html: menuOption?.label }}
                />

                {menuOption?.type === "filter-suboption" && (
                  <span className="pl-[2px] text-yellow-light planet:text-gray-generic">
                    ({menuOption?.list.length})
                  </span>
                )}
              </div>
              <div className="flex justify-between items-center gap-4">
                {menuOption?.type === "filter-option" && (
                  <div
                    className="text-xs text-white planet:text-gray-generic"
                    dangerouslySetInnerHTML={{
                      __html: optionsBrief(menuOption?.label)
                    }}
                  />
                )}
                <ArrowRight
                  className={`stroke-white planet:stroke-gray-generic ${
                    menuOption?.type === "filter-suboption"
                      ? currentIndex === index && isOpened
                        ? "-rotate-90"
                        : "rotate-90"
                      : ""
                  }`}
                />
              </div>
            </button>
            <div className="divide-y width-divider !border-white planet:!border-gray-generic" />
            <div
              ref={refs[index]}
              className="overflow-hidden transition-all duration-200 h-0"
            >
              {menuOption?.type === "filter-suboption" &&
                menuOption?.list?.map((item, index) => (
                  <div
                    className="flex items-center justify-between pt-5"
                    key={index}
                    onClick={handleSelect(item)}
                  >
                    <div
                      className="text-xs text-white flex items-center"
                      dangerouslySetInnerHTML={{ __html: item?.label }}
                    />
                    <TickSVG
                      className={`block  ${
                        // For multiple options
                        // selectedItems[item.filter]?.includes(item.value)
                        // NOTE: programPrice range is formatted differently, its an object and not string like the others
                        selectedItems[item?.filter]?.value === item?.value ||
                        selectedItems[item?.filter] === item?.value
                          ? "!stroke-white fill-white"
                          : "planet:stroke-gray-generic planet:fill-gray-generic stroke-yellow-light fill-yellow-light"
                      }`}
                    />
                  </div>
                ))}
            </div>
          </React.Fragment>
        ))}
    </div>
  );
};
